import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiMapPin, FiLinkedin, FiTwitter } = FiIcons;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const handleLinkClick = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-gray text-clean-white py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <motion.div 
              className="flex items-center space-x-3 mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-dental-blue to-ai-teal rounded-xl flex items-center justify-center">
                <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1757516319809-Favicon%20White.PNG" alt="EngageDent Ai Logo" className="w-32 h-32 object-contain" />
              </div>
              <span className="text-2xl font-bold">EngageDent Ai</span>
            </motion.div>
            <motion.p 
              className="text-clean-white opacity-80 leading-relaxed max-w-md mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Transforming dental practices with AI-powered solutions that fill chairs, streamline operations, and grow bottom lines.
            </motion.p>
            <motion.div 
              className="flex space-x-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <a href="#" className="w-10 h-10 bg-dental-blue rounded-lg flex items-center justify-center hover:bg-ai-teal transition-colors">
                <SafeIcon icon={FiLinkedin} className="text-clean-white" />
              </a>
              <a href="#" className="w-10 h-10 bg-dental-blue rounded-lg flex items-center justify-center hover:bg-ai-teal transition-colors">
                <SafeIcon icon={FiTwitter} className="text-clean-white" />
              </a>
            </motion.div>
          </div>

          {/* Quick Links */}
          <div>
            <motion.h3 
              className="text-lg font-semibold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              Quick Links
            </motion.h3>
            <motion.div 
              className="space-y-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              {['Products', 'ROI Calculator', 'Demo', 'Contact'].map((link) => (
                <div key={link}>
                  <a href={`#${link.toLowerCase().replace(' ', '')}`} className="text-clean-white opacity-80 hover:opacity-100 transition-opacity">
                    {link}
                  </a>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Contact Info */}
          <div>
            <motion.h3 
              className="text-lg font-semibold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              Contact
            </motion.h3>
            <motion.div 
              className="space-y-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMail} className="text-ai-teal" />
                <span className="text-clean-white opacity-80">info@engagedent.com</span>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMapPin} className="text-ai-teal" />
                <span className="text-clean-white opacity-80">Boston, MA</span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <motion.div 
          className="border-t border-clean-white border-opacity-20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-clean-white opacity-60 mb-4 md:mb-0">
            © {currentYear} EngageDent Ai. All rights reserved.
          </p>
          <div className="flex space-x-6">
            <button 
              onClick={() => handleLinkClick('/privacy-policy')}
              className="text-clean-white opacity-60 hover:opacity-100 transition-opacity underline text-sm"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => handleLinkClick('/terms')}
              className="text-clean-white opacity-60 hover:opacity-100 transition-opacity underline text-sm"
            >
              Terms & Conditions
            </button>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;